import { createSelector } from '@reduxjs/toolkit'
import type { State } from '../index.js'
import type { SinkConfig, SinkId, SourceId, SpinSelection, VizSourceEntity } from './VisualizationSliceTypes.js'
import type { BlastSource } from '../../../Model/Project/BlastSource.js'
import { cubeStateKey } from './VisualizationSlice.js'
import type { CubeStatus } from './VisualizationSlice.js'

export type FlatSink = {
  sourceId: SourceId
  sinkId: SinkId
  config: SinkConfig
  source: VizSourceEntity
}

const selectSources = (s: typeof State) => s.visualization.sources
const selectScopeId = (s: typeof State) => s.visualization.scope?.id ?? null
const selectCubes = (s: typeof State) => s.visualization.cubes

/** One entry per source: the cube it depends on and that cube's current status. */
export type SourceCubeRef = {
  sourceId: SourceId
  scopeId: string
  blastType: BlastSource
  /** Present when the source draws from a SPIN cube; part of the cube identity/table lookup. */
  spin?: SpinSelection
  /** `'absent'` when the cube has not been requested yet (no slice entry). */
  status: CubeStatus | 'absent'
  /** Whether the source's FE pipeline has already produced buckets. */
  hasBuckets: boolean
}

/**
 * Resolves, for the current scope, which cube each source is waiting on and that cube's status.
 * Drives the workspace-level "no precomputed data" notice (see `WorkspaceDataNotice`) and any
 * other consumer that needs a scope-wide readiness picture without reaching into the registry.
 */
export const selectSourceCubeRefs = createSelector(
  [selectSources, selectScopeId, selectCubes],
  (sources, scopeId, cubes): SourceCubeRef[] => {
    if (!scopeId) return []
    const out: SourceCubeRef[] = []
    for (const [sourceId, src] of Object.entries(sources)) {
      const blastType = src?.shared?.blastType
      if (!blastType) continue
      const spin = src?.shared?.spin
      const entry = cubes[cubeStateKey(scopeId, blastType, spin)]
      out.push({
        sourceId: sourceId as SourceId,
        scopeId,
        blastType,
        spin,
        status: entry?.status ?? 'absent',
        hasBuckets: !!src?.snapshot
      })
    }
    return out
  }
)

export const selectAllSinks = createSelector([selectSources], (sources): FlatSink[] => {
  const out: FlatSink[] = []
  for (const [sourceId, src] of Object.entries(sources)) {
    for (const [sinkId, entry] of Object.entries(src.sinks)) {
      const cfg = entry?.config
      if (!cfg) continue
      out.push({
        sourceId: sourceId as SourceId,
        sinkId: sinkId as SinkId,
        config: cfg,
        source: src
      })
    }
  }
  return out
})
